"use client";

import { format } from "date-fns";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SelectLabel,
} from "../ui/select";
import { ChevronLeft, ChevronRight, SearchIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { createClient } from "@/utils/supabase/client";
import { renderPageButtons } from "./pagination";
import { useToast } from "@/hooks/use-toast";

const perPageOptions = [10, 20, 50, 100];

export default function Table({
  headerBackground,
  rounds,
  campaign,
  date,
}: {
  headerBackground: string;
  rounds: any;
  campaign: any;
  date?: number;
}) {
  const { toast } = useToast();
  const [participants, setParticipants] = useState<any>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [selectedRound, setSelectedRound] = useState<string>(rounds?.[0]?.id);
  const [search, setSearch] = useState<string>("");
  const [perPage, setPerPage] = useState<number>(perPageOptions?.[0]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  useEffect(() => {
    getParticipants();
  }, [selectedRound, perPage, currentPage, date]);

  const getParticipants = async () => {
    const supabase = await createClient();
    const { data, error } = await supabase.rpc("get_winners_masked", {
      p_campaign_id: campaign.id,
      p_search: search,
      p_round_id: selectedRound,
      p_limit: perPage,
      p_offset: (currentPage - 1) * perPage,
    });
    if (error) {
      toast({
        variant: "destructive",
        title: "Rất tiếc",
        description: "Chưa có danh sách người trúng thưởng",
      })
      return;
    }
    if (data?.total_count > 0) {
      setParticipants(data?.data);
      setTotalCount(Number(data.total_count));
    } else {
      setParticipants([]);
      setTotalCount(0);
    }
  };

  return (
    <div className="flex flex-col gap-4 h-full">
      <div className="flex items-center gap-4 justify-center text-black">
        <Select
          value={selectedRound?.toString()}
          onValueChange={(value) => setSelectedRound(value)}
        >
          <SelectTrigger
            className="w-[300px] bg-white"
            style={{ height: "48px" }}
          >
            <SelectValue placeholder="Lựa chọn vòng" />
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              <SelectLabel>Lựa chọn vòng</SelectLabel>
              {rounds?.map((item: any, index: number) => (
                <SelectItem key={index} value={item.id.toString()}>
                  <b>Vòng {index + 1}</b> ({format(item.from_at, "dd/MM/yyyy")}{" "}
                  - {format(item.to_at, "dd/MM/yyyy")})
                </SelectItem>
              ))}
            </SelectGroup>
          </SelectContent>
        </Select>
        <div className="flex items-center gap-1 w-[275px] relative">
          <Input
            className="bg-white h-12 pr-12"
            placeholder="Tìm kiếm"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <div onClick={() => getParticipants()} className="cursor-pointer absolute right-4 top-1/2 -translate-y-1/2">
            <SearchIcon className="w-6 h-6" />
          </div>
        </div>
      </div>
      <div className="flex flex-col gap-2">
        <div className="overflow-x-auto -m-1">
          <table className="w-full text-md border-separate border-spacing-y-1 border-spacing-x-1">
            {/* Header */}
            <thead>
              <tr
                className="text-white text-center rounded-sm"
                style={{ backgroundColor: headerBackground }}
              >
                <th className="p-4 font-semibold rounded-sm w-[60px]">STT</th>
                <th className="p-4 font-semibold rounded-sm">Tên Khách hàng</th>
                <th className="p-4 font-semibold rounded-sm">Số điện thoại</th>
                <th className="p-4 font-semibold rounded-sm">
                  Ngày trúng thưởng
                </th>
                <th className="p-4 font-semibold rounded-sm">Giải thưởng</th>
              </tr>
            </thead>

            {/* Body */}
            <tbody>
              {participants?.length === 0 ? (
                <tr key="no-data">
                  <td
                    colSpan={5}
                    className="text-center p-20 text-red-500 font-semibold bg-white rounded-sm"
                  >
                    Không có dữ liệu
                  </td>
                </tr>
              ) : (
                participants?.map((row: any, index: number) => (
                  <tr key={row.winner_id} className="text-center rounded-sm" style={{ color: 'black' }}>
                    <td className="p-4 rounded-sm bg-gray-100 w-[60px]">
                      {index + 1}
                    </td>
                    <td className="p-4 rounded-sm bg-gray-100">{row.name}</td>
                    <td className="p-4 rounded-sm bg-gray-100">{row.phone}</td>
                    <td className="p-4 rounded-sm bg-gray-100">
                      {format(row.created_at, "dd/MM/yyyy")}
                    </td>
                    <td className="p-4 rounded-sm bg-gray-100">
                      <b>{row.prize_name}</b>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div className="px-3 py-3 border rounded-sm w-full flex items-center justify-between bg-white">
          <div className="flex items-center gap-4 text-black">
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setCurrentPage(Math.max(currentPage - 1, 1))}
                disabled={currentPage === 1}
              >
                <ChevronLeft />
              </Button>

              {renderPageButtons(
                totalCount,
                perPage,
                currentPage,
                setCurrentPage
              )}

              <Button
                variant="ghost"
                size="icon"
                onClick={() =>
                  setCurrentPage(
                    Math.min(currentPage + 1, Math.ceil(totalCount / perPage))
                  )
                }
                disabled={currentPage === Math.ceil(totalCount / perPage)}
              >
                <ChevronRight />
              </Button>
            </div>
            <div className="flex items-center gap-2">
              <Select
                value={perPage.toString()}
                onValueChange={(value) => setPerPage(parseInt(value))}
              >
                <SelectTrigger className="bg-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    {perPageOptions?.map((item: any, index: number) => (
                      <SelectItem key={index} value={item.toString()}>
                        {item}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
              <span className="text-sm">{perPage} dòng trên mỗi trang</span>
            </div>
          </div>
          <div className="flex items-center gap-2 text-sm">
            Tổng {participants?.length} dòng
          </div>
        </div>
      </div>
    </div>
  );
}
